/**
 * @file WebSocket Broadcast Handler
 * @description Handles WebSocket broadcast messages with OpenTelemetry tracing, logging, and metric recording.
 */

import { tracer } from '../../tracers';
import { WS_BROADCAST_NAME, WS_HUB_SERIAL } from '../../constants/attributeNames';
import { log } from '../../logging';
import { HUB_BROADCAST, SEVERITY_TEXT, STATUSES } from '../../constants';
import { getDynamicWSLogAttributes } from '../../utils/getDynamicWSLogAttributes';
import { withSafelyExecute } from '../../utils/withSafelyExecute';
import { getEnvKey } from 'constants/envs';
import { otlHandleWsBroadcastMetrics } from '../../utils/otlHandleWsBroadcastMetrics';
import { calculateSizeInBytes } from '../../utils';
import { getWsBroadcastParentContext } from '../../utils/getWsBroadcastParentContext';

/**
 * Handles a WebSocket broadcast message with OpenTelemetry tracing, logging, and metrics.
 *
 * @param {string} serial - The serial number of the hub that sent the broadcast.
 * @param {string} name - The name of the WebSocket broadcast event.
 * @param {Array} args - An array where the first element is the broadcast data payload.
 * @param {Object} data - The broadcast data payload.
 * @param {Object} [data.ot] - OpenTelemetry context object.
 * @param {string} [data.ot.trace] - Trace ID from the broadcast data.
 * @param {string} [data.ot.span] - Span ID from the broadcast data.
 */
function broadcastWithOtl(serial, name, [data]) {
    const parentContext = getWsBroadcastParentContext(data?.ot);

    tracer.startActiveSpan(
        `WS ${name} broadcast`,
        { attributes: { [WS_HUB_SERIAL]: serial, [WS_BROADCAST_NAME]: name, environment: getEnvKey() } },
        parentContext,
        (span) => {
            const dataSizeInBytes = calculateSizeInBytes(data);
            otlHandleWsBroadcastMetrics(name, dataSizeInBytes);
            log(`Received WS ${name} broadcast`, SEVERITY_TEXT.INFO, {
                hub_serial: serial,
                type: HUB_BROADCAST,
                status: STATUSES.OK,
                api: name,
                ...getDynamicWSLogAttributes({ serial, data, dataSizeInBytes }),
            });

            span.end();
        },
    );
}

export default withSafelyExecute(broadcastWithOtl);
